/**
 * A thread, next to its pin — and a new one, being written (session 55,
 * `.docs/55-mockup-comments.md` §Threads).
 *
 * The card is drawn by the portal **over** the mockup's frame, never inside it: a comment's
 * words never enter the frame. It is placed by where the frame's bridge says the pin is
 * (`usePinBridge` `where`), so it follows the page as it scrolls, and kept inside the page's
 * area (`review.ts` `cardPlace`); on a phone it is a sheet along the bottom.
 *
 * What a person may do in it is what the server said they may (`viewer.may_*`): reply, resolve
 * and reopen, and — the studio's managers — hide a guest's words.
 */

import type {
  MockupCommentView,
  MockupLinkViewer,
  MockupPin,
  MockupReplyView,
} from '@fareast/shared'
import { MOCKUP_COMMENT_MAX, MOCKUP_GUEST_NAME_MAX } from '@fareast/shared'
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { z } from 'zod'

import { messageOf } from '../../api/problem.js'
import { Button, ButtonRow } from '../../components/portal/Button.js'
import { PinCard, PinNo } from '../../components/portal/Files.js'
import { Input, Textarea, useZodForm } from '../../components/portal/Form.js'
import { Push, Row, Stack } from '../../components/portal/Layout.js'
import { Notice, Tag } from '../../components/portal/Status.js'
import { B, Muted, Prose, Small, Xs } from '../../components/portal/Text.js'
import { ActionDialog } from '../../components/studio/ActionDialog.js'
import { useFormat } from '../../format/useFormat.js'
import type { FramePoint } from '../../pins/usePinBridge.js'
import type { NewComment } from './doors.js'
import { excerptOf } from './pins.js'
import { cardPlace } from './review.js'
import { ENTER_HINT, aboutWords } from './words.js'

/* ------------------------------------------------------------------ the place */

/**
 * A `PinCard`, placed: beside the pin the frame reports, inside the page's area — measured after
 * every render, because a reply makes it taller and the page scrolling moves its pin.
 */
export function PlacedCard({
  where,
  frame,
  area,
  label,
  children,
}: {
  /** The pin's tip in the frame's viewport, or null to sit in the area's corner. */
  readonly where: FramePoint | null
  readonly frame: RefObject<HTMLIFrameElement | null>
  readonly area: RefObject<HTMLDivElement | null>
  readonly label: string
  readonly children: ReactNode
}) {
  const card = useRef<HTMLElement | null>(null)
  const [at, setAt] = useState({ left: 0, top: 0 })
  // The page's area changing — the window resizing, the list opening beside it — moves the card
  // without the frame saying anything new.
  const [resized, setResized] = useState(0)
  useEffect(() => {
    const onResize = () => {
      setResized((count) => count + 1)
    }
    window.addEventListener('resize', onResize)
    const observer =
      typeof ResizeObserver === 'function' && area.current !== null
        ? new ResizeObserver(onResize)
        : null
    if (area.current !== null) observer?.observe(area.current)
    return () => {
      window.removeEventListener('resize', onResize)
      observer?.disconnect()
    }
  }, [area])

  // A reply makes the card taller, and a taller card near the bottom has to move up.
  const [size, setSize] = useState({ width: 0, height: 0 })
  useLayoutEffect(() => {
    const element = card.current
    if (element === null) return
    const measure = () => {
      const next = { width: element.offsetWidth, height: element.offsetHeight }
      setSize((last) => (last.width === next.width && last.height === next.height ? last : next))
    }
    measure()
    if (typeof ResizeObserver !== 'function') return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => {
      observer.disconnect()
    }
  }, [])

  useLayoutEffect(() => {
    const frameBox = frame.current?.getBoundingClientRect()
    const areaBox = area.current?.getBoundingClientRect()
    if (frameBox === undefined || areaBox === undefined) return
    const next = cardPlace({
      tip: where === null ? null : { x: frameBox.left + where.x, y: frameBox.top + where.y },
      area: areaBox,
      card: size,
    })
    setAt((last) => (last.left === next.left && last.top === next.top ? last : next))
  }, [where, size, resized, frame, area])

  return (
    <PinCard at={at} label={label} cardRef={card}>
      {children}
    </PinCard>
  )
}

/* ------------------------------------------------------------------ the words */

/** What a thread is about, under its head: the text quoted, or a few words. */
function About({ pin }: { readonly pin: MockupPin | null }) {
  const words = aboutWords(pin)
  if (pin?.quote !== undefined) {
    return (
      <Small>
        <Muted>&ldquo;{excerptOf(pin.quote, 160)}&rdquo;</Muted>
      </Small>
    )
  }
  return words === null ? null : (
    <Small>
      <Muted>{words}</Muted>
    </Small>
  )
}

/** One thing somebody said: who, when, and the words — the studio's, a guest's, hidden. */
function Said({
  said,
  tools,
}: {
  readonly said: MockupReplyView
  readonly tools?: ReactNode | undefined
}) {
  const format = useFormat()
  return (
    <Stack gap="tight">
      <Row>
        <Small>
          <B>{said.author}</B>
          <Muted>
            {' '}
            &middot; {said.from_studio ? 'Far East Studios · ' : ''}
            {format.date(said.created_at)}
          </Muted>
        </Small>
        {said.guest ? <Tag>Guest</Tag> : null}
        {said.hidden_at === null ? null : <Tag>Hidden</Tag>}
        <Push />
        {tools}
      </Row>
      <Prose>{said.hidden_at === null ? said.body : <Muted>{said.body}</Muted>}</Prose>
    </Stack>
  )
}

/* ------------------------------------------------------------------- the box */

/** One shape for every box: a member's never shows the name, and a guest's requires it. */
const BoxSchema = z.object({
  body: z.string().trim().min(1, 'Write something first.').max(MOCKUP_COMMENT_MAX),
  name: z.string().trim().max(MOCKUP_GUEST_NAME_MAX),
})

/**
 * A comment box — a new thread's or a reply's. A guest is asked their name once, and it is
 * remembered on this device (`guestName`); after that it is a line that says who they are
 * commenting as.
 */
function CommentBox({
  label,
  submit,
  guestName,
  autoFocus,
  onPosted,
  onCancel,
}: {
  readonly label: string
  readonly submit: string
  /** Present for a guest: the name they gave last, or '' for none yet. */
  readonly guestName: string | undefined
  readonly autoFocus?: boolean | undefined
  readonly onPosted: (values: { readonly body: string; readonly name?: string }) => Promise<void>
  readonly onCancel?: (() => void) | undefined
}) {
  const guest = guestName !== undefined
  const form = useZodForm(BoxSchema, { defaultValues: { body: '', name: guestName ?? '' } })
  const [askName, setAskName] = useState(guest && guestName === '')
  const [failure, setFailure] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        void form.handleSubmit(async (values) => {
          if (guest && values.name === '') {
            setAskName(true)
            form.setError('name', { message: 'Say who you are first.' })
            return
          }
          setFailure(null)
          setBusy(true)
          try {
            await onPosted(guest ? values : { body: values.body })
            form.reset({ ...values, body: '' })
            setAskName(false)
          } catch (error) {
            setFailure(messageOf(error))
          } finally {
            setBusy(false)
          }
        })(event)
      }}
    >
      <Stack gap="tight">
        {guest && askName ? (
          <Input control={form.control} name="name" label="Your name" autoComplete="name" />
        ) : null}
        <Textarea
          control={form.control}
          name="body"
          label={label}
          placeholder="What works, what does not, what you would change"
          submitOnEnter
          autoFocus={autoFocus}
        />
        {guest && !askName ? (
          <Xs>
            <Muted>As {form.getValues('name')} &middot; </Muted>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setAskName(true)
              }}
            >
              Change name
            </Button>
          </Xs>
        ) : null}
        {failure === null ? null : (
          <Notice need role="alert">
            {failure}
          </Notice>
        )}
        <Row>
          <ButtonRow>
            <Button type="submit" variant="primary" size="sm" disabled={busy}>
              {busy ? 'Posting…' : submit}
            </Button>
            {onCancel === undefined ? null : (
              <Button size="sm" variant="ghost" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </ButtonRow>
          <Push />
          <Xs>
            <Muted>{ENTER_HINT}</Muted>
          </Xs>
        </Row>
      </Stack>
    </form>
  )
}

/* -------------------------------------------------------------- a new thread */

export function NewThread({
  pin,
  guestName,
  onPost,
  onClose,
}: {
  readonly pin: MockupPin | null
  readonly guestName: string | undefined
  readonly onPost: (comment: Pick<NewComment, 'body' | 'name'>) => Promise<void>
  readonly onClose: () => void
}) {
  return (
    <Stack gap="tight">
      <Row>
        <B>New comment</B>
        <Push />
        <Button size="sm" variant="ghost" onClick={onClose}>
          Close
        </Button>
      </Row>
      <About pin={pin} />
      <CommentBox
        label="Comment"
        submit="Post"
        guestName={guestName}
        autoFocus
        onPosted={async (values) => {
          await onPost(values)
          onClose()
        }}
        onCancel={onClose}
      />
    </Stack>
  )
}

/* ------------------------------------------------------------------ a thread */

export function Thread({
  thread,
  n,
  viewer,
  guestName,
  onReply,
  onResolve,
  onReopen,
  onClose,
  notice,
}: {
  readonly thread: MockupCommentView
  /** Its pin's number, or undefined for a thread about the whole version. */
  readonly n: number | undefined
  readonly viewer: MockupLinkViewer
  readonly guestName: string | undefined
  readonly onReply: (comment: Pick<NewComment, 'body' | 'name'>) => Promise<void>
  readonly onResolve: () => Promise<void>
  readonly onReopen: () => Promise<void>
  readonly onClose: () => void
  /** Said under its head — where its pin is, when that is not on the page shown. */
  readonly notice?: ReactNode | undefined
}) {
  const format = useFormat()
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const resolved = thread.resolved_at !== null
  const hidden = thread.hidden_at !== null

  async function decide(what: () => Promise<void>) {
    setBusy(true)
    setFailure(null)
    try {
      await what()
    } catch (error) {
      setFailure(messageOf(error))
    } finally {
      setBusy(false)
    }
  }

  /** The studio's one tool on somebody's words: hiding a guest's (session 49). */
  const hideTool = (said: MockupReplyView) =>
    viewer.may_hide && said.guest && said.hidden_at === null ? (
      <ActionDialog
        action="mockups_hide_comment"
        label="Hide"
        title={`Hide ${said.author}’s comment`}
        submitLabel="Hide it"
        variant="ghost"
        size="sm"
        fixed={{ comment_id: said.id }}
      >
        The client and anybody with the link stop seeing it. It stays here, marked hidden, and the
        reason goes on the record.
      </ActionDialog>
    ) : null

  return (
    <Stack gap="tight">
      <Row>
        {n === undefined ? <B>Comment</B> : <PinNo n={n} done={resolved} />}
        {resolved ? <Tag>Resolved</Tag> : null}
        <Push />
        {viewer.may_resolve && !hidden ? (
          <Button
            size="sm"
            variant={resolved ? 'ghost' : 'default'}
            disabled={busy}
            onClick={() => {
              void decide(resolved ? onReopen : onResolve)
            }}
          >
            {resolved ? 'Reopen' : 'Resolve'}
          </Button>
        ) : null}
        <Button size="sm" variant="ghost" onClick={onClose}>
          Close
        </Button>
      </Row>
      <About pin={thread.pin} />
      {notice}
      {resolved ? (
        <Xs>
          <Muted>
            Resolved{thread.resolved_by === null ? '' : ` by ${thread.resolved_by}`} on{' '}
            {format.date(thread.resolved_at ?? thread.created_at)}.
          </Muted>
        </Xs>
      ) : null}
      {failure === null ? null : (
        <Notice need role="alert">
          {failure}
        </Notice>
      )}
      <Said said={thread} tools={hideTool(thread)} />
      {thread.replies.map((reply) => (
        <Said key={reply.id} said={reply} tools={hideTool(reply)} />
      ))}
      {viewer.may_comment && !hidden ? (
        <CommentBox
          // A reply typed in one thread is not posted into the next one opened.
          key={thread.id}
          label="Reply"
          submit="Reply"
          guestName={guestName}
          onPosted={onReply}
        />
      ) : null}
    </Stack>
  )
}
